import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import type { ClickwrapTerms } from "../clickwrap/use-clickwrap-terms";
import { ExosIcon } from "./exos-icon";

interface RegisterInterestDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  clickwrapTerms: ClickwrapTerms;
}

const openLinksInNewTab = (html: string): string => {
  const document = new DOMParser().parseFromString(html, "text/html");

  document.querySelectorAll("a").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  return document.body.innerHTML;
};

export const RegisterInterestDialog: FC<RegisterInterestDialogProps> = ({
  open,
  onClose,
  onAccept,
  clickwrapTerms,
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "white",
          background: "radial-gradient(96.78% 141.42% at 0% 0%, #582F79 0%, #231335 100%)",
        }}
      >
        <ExosIcon />
        EXOS - register your interest
      </DialogTitle>
      <DialogContent dividers>
        {clickwrapTerms.status === "loading" ? (
          <Box>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2">Loading latest agreement terms...</Typography>
          </Box>
        ) : null}
        {clickwrapTerms.status === "error" ? (
          <Alert severity="error">
            {clickwrapTerms.errorMessage ?? "Couldn’t load the agreement. Please retry."}
          </Alert>
        ) : null}
        {clickwrapTerms.status === "loaded" ? (
          <>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                We’re getting EXOS ready for launch and would love to keep you updated. By
                registering your interest, you agree to the service terms.
              </Typography>
              <Typography variant="body2">
                <Typography component="span" variant="inherit" fontWeight={700}>
                  Already using Pages and FDE?
                </Typography>{" "}
                - you must accept the terms to continue with the service.
              </Typography>
            </Box>
            <Box
              sx={{
                maxHeight: "60vh",
                overflowY: "auto",
                "& h1, & h2, & h3, & h4": { mt: 0 },
                "& p, & li": { whiteSpace: "pre-wrap" },
              }}
              data-content-format={clickwrapTerms.contentFormat}
              dangerouslySetInnerHTML={{ __html: openLinksInNewTab(clickwrapTerms.contentHtml) }}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-analytics-id="exos-register-interest-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          data-analytics-id="exos-register-interest-accept"
          onClick={() => {
            void onAccept();
            onClose();
          }}
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};
